import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

/**
 * House Pricing Data for a single property
 */
export interface HousePricingData {
	supId: string;
	constructionRate: number;
	constructionPrice: number;
	plotRate: number;
	plotPrice: number;
}

/**
 * State interface for House Pricing Store
 */
export interface HousePricingState {
	// Record of supId -> HousePricingData for all properties (using Record instead of Map for better reactivity)
	pricingData: Record<string, HousePricingData>;

	// Actions
	setPricingData: ({ supId, data }: { supId: string; data: HousePricingData }) => void;
	updateConstructionRate: ({ supId, constructionRate }: { supId: string; constructionRate: number }) => void;
	getPricingData: ({ supId }: { supId: string }) => HousePricingData | undefined;
	clearPricingData: () => void;
}

/**
 * Store for managing house pricing data (construction rate, plot rate, etc.)
 * Enables real-time updates across components
 */
export const useHousePricingStore = create<HousePricingState>()(
	devtools(
		subscribeWithSelector(
			immer((set, get) => ({
				pricingData: {},

				// Set complete pricing data for a property
				setPricingData: ({ supId, data }) =>
					set(state => {
						state.pricingData[supId] = data;
					}),

				// Update only construction rate and recalculate prices
				updateConstructionRate: ({ supId, constructionRate }) =>
					set(state => {
						const existing = state.pricingData[supId];
						if (existing) {
							state.pricingData[supId] = {
								...existing,
								constructionRate,
								// Note: constructionPrice and plotPrice should be recalculated
								// by the component that has access to builtUpArea, plotArea, and total price
							};
						}
					}),

				// Get pricing data for a specific property
				getPricingData: ({ supId }: { supId: string }): HousePricingData | undefined => {
					return get().pricingData[supId];
				},

				// Clear all pricing data
				clearPricingData: () =>
					set(state => {
						state.pricingData = {};
					}),
			})),
		),
		{ name: "house-pricing-store" },
	),
);
